/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import NiceModal, { useModal } from '@ebay/nice-modal-react';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton } from '@mui/material';

type ConfirmationModalProps = {
	title: string;
	description: string | null;
	acceptButtonText: string;
	declineButtonText: string;
	onAccept: () => void;
};

export const ConfirmationModal = NiceModal.create( ( {
	onAccept,
	title,
	description,
	acceptButtonText,
	declineButtonText
}: ConfirmationModalProps ) => {
	const modal = useModal();

	function handleClose() {
		return modal.hide();
	}

	function handleAccept() {
		onAccept();

		return modal.hide();
	}

	return (
		<Dialog
			open={ modal.visible }
			keepMounted
			onClose={ handleClose }
			aria-describedby="alert-dialog-slide-description"
			sx={ { padding: '20px' } }
		>
			<IconButton
				aria-label="close"
				onClick={ handleClose }
				sx={ {
					position: 'absolute',
					right: 8,
					top: 8,
					color: 'grey.500'
				} }
			>
				<CloseIcon/>
			</IconButton>
			<DialogContent sx={ { display: 'flex', flexFlow: 'column', alignItems: 'center' } }>
				<DialogTitle sx={ { display: 'flex', justifyContent: 'center', marginTop: '12px' } }>
					{ title }
				</DialogTitle>
				{ description && (
					<DialogContentText sx={ { maxWidth: '350px', display: 'flex', justifyContent: 'center', textAlign: 'center' } }>
						{ description }
					</DialogContentText>
				) }
			</DialogContent>
			<DialogActions sx={ { display: 'flex', gap: '20px', padding: '20px' } }>
				<Button onClick={ handleClose } variant={ 'outlined' } sx={ { flexGrow: '1' } }>{ declineButtonText }</Button>
				<Button onClick={ handleAccept } variant={ 'contained' } sx={ { flexGrow: '1' } }>{ acceptButtonText }</Button>
			</DialogActions>
		</Dialog>
	);
} );
